<template>
    <my-page :loading="isLoading">
        <template #heading>{{ $t('MY_BILLING') }}</template>

        <v-tabs v-model="tab" :tabs="tabs">
            <template #subscriptions>
                <p v-if="!subscriptions.length">{{ $t('NO_SUBSCRIPTIONS_MESSAGE') }}</p>
                <div v-else class="entries">
                  <v-entry
                    v-for="subscription of subscriptions"
                    :icon="subscription.icon_uri"
                    :name="subscription.name"
                    :status="subscription.status_translated"
                    :status-severity="/active|cancelled/.test(subscription.status) ? 'success' : 'error'"
                    :key="subscription.identifier"
                    @click.native="showSubscriptionModal(subscription)"
                  />
                </div>
            </template>

            <template #transactions>
              <p v-if="!transactions.length">{{ $t('NO_TRANSACTIONS_MESSAGE') }}</p>
                <div v-else class="entries">
                  <v-entry
                    v-for="transaction of transactions"
                    :icon="transaction.icon_uri"
                    :name="transaction.name"
                    :status="transaction.status_translated"
                    :status-severity="/refunded/.test(transaction.status) ? 'error' : 'success'"
                    :key="transaction.identifier"
                    @click.native="showTransactionModal(transaction)"
                  />
                </div>
            </template>
        </v-tabs>

        <v-transaction-modal @transaction-updated="onTransactionUpdated" />
        <v-subscription-modal @subscription-updated="onSubscriptionUpdated" />
    </my-page>
</template>

<script>
import VTabs from '@/components/VTabs.vue';
import MyPage from '../components/MyPage.vue';
import VSubscriptionModal from './components/VSubscriptionModal.vue';
import VTransactionModal from './components/VTransactionModal.vue';
import VEntry from './components/VEntry.vue';

export default {
  components: {
    MyPage,
    VTabs,
    VEntry,
    VSubscriptionModal,
    VTransactionModal,
  },
  data() {
    return {
      isLoading: true,
      tab: 'subscriptions',
      subscriptions: [],
      transactions: [],
    };
  },
  computed: {
    title() {
      return this.$t('MY_BILLING');
    },
    tabs() {
      return [
        {
          key: 'subscriptions',
          name: this.$t('SUBSCRIPTIONS'),
        },
        {
          key: 'transactions',
          name: this.$t('TRANSACTIONS'),
        },
      ];
    },
  },
  methods: {
    async getSubscriptions() {
      this.subscriptions = await this.$api.getSubscriptions();
    },
    async getTransactions() {
      this.transactions = await this.$api.getTransactions();
    },
    async load() {
      this.isLoading = true;
      try {
        await Promise.all([
          this.getSubscriptions(),
          this.getTransactions(),
        ]);
      } catch {
        this.$router.push({ name: 'error' });
      } finally {
        this.isLoading = false;
      }
    },
    showSubscriptionModal(subscription) {
      this.$modal.show('subscription', { data: subscription });
    },
    showTransactionModal(transaction) {
      const subscription = this.subscriptions.find(sub => sub.purchase_identifier === transaction.identifier);
      this.$modal.show('transaction', { transaction, subscription });
    },
    handleTabUrl() {
      for (const tab of this.tabs) {
        if (tab.key in this.$route.query) {
          this.tab = tab.key;
          break;
        }
      }
    },
    handleSubscriptionUrl() {
      if ('subscription' in this.$route.query) {
        const subscription = this.subscriptions.find(sub => sub.purchase_identifier === this.$route.query.subscription);
        if (subscription) {
          this.showSubscriptionModal(subscription);
        }
      }
    },
    handleTransactionUrl() {
      if ('transaction' in this.$route.query) {
        const transaction = this.transactions.find(trans => trans.identifier === this.$route.query.transaction);
        if (transaction) {
          this.showTransactionModal(transaction);
        }
      }
    },
    handleUrls() {
      this.handleTabUrl();
      this.handleSubscriptionUrl();
      this.handleTransactionUrl();
    },
    async onSubscriptionUpdated(updatedSubscription) {
      const index = this.subscriptions.findIndex(sub => sub.purchase_identifier === updatedSubscription.purchase_identifier);
      if (index !== -1) {
        this.$set(this.subscriptions, index, updatedSubscription);
      }
    },
    async onTransactionUpdated(updatedTransaction) {
      const index = this.transactions.findIndex(trans => trans.identifier === updatedTransaction.identifier);
      if (index !== -1) {
        this.$set(this.transactions, index, updatedTransaction);
      }
    },
  },
  created() {
    this.load().then(this.handleUrls);
  },
};
</script>

<style lang="scss" scoped>
.entries {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 15px;
}
</style>
